import { diffSequence, coalesce } from './wordDiff';

// Word diff for rich-text (TinyMCE HTML) values that keeps strike-through as a
// per-word flag. Sales record a PIF/CO edit by striking the old words through
// instead of deleting them, so "120 170 mm" → "<s>120</s> 170 mm" is a real
// change even though the plain text is identical.
//
// Mirror of Crm::Project::Diff::RichText on the backend — keep the strike rule
// in sync, the backend decides which fields count as changed.

const STRIKE_TAGS = new Set(['S', 'DEL', 'STRIKE']);
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT']);
// `text-decoration-line: line-through` too; Excel's `text-line-through: none` is not a strike.
const STRIKE_STYLE = /(?:^|[;\s])text-decoration(?:-line)?\s*:[^;]*line-through/i;
// Only values that really carry a tag or an entity are parsed: a bare "<" in plain
// text ("Deflection <L/180") would otherwise open a tag that swallows the rest.
const MARKUP = /<\/?[a-z][a-z0-9]*(?:\s[^<>]*)?\/?>|&(?:#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/i;
const SPACES = /\s+/;

const words = (text, struck) => text.split(SPACES).filter(Boolean).map((w) => ({ text: w, struck }));

const ancestorsOf = (node) => {
  const out = [];
  for (let el = node.parentElement; el; el = el.parentElement) out.push(el);
  return out;
};

const isStruck = (ancestors) => ancestors.some(
  (el) => STRIKE_TAGS.has(el.tagName) || STRIKE_STYLE.test(el.getAttribute('style') || '')
);

// → [{ text, struck }], one entry per word. DOMParser builds a detached tree:
// no scripts run, no requests fire.
export const toDiffTokens = (value) => {
  const str = value == null ? '' : String(value);
  if (!MARKUP.test(str) || typeof window === 'undefined' || typeof window.DOMParser !== 'function') {
    return words(str, false);
  }

  const doc = new window.DOMParser().parseFromString(`<body>${str}</body>`, 'text/html');
  const walker = doc.createTreeWalker(doc.body, window.NodeFilter.SHOW_TEXT);
  const tokens = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const ancestors = ancestorsOf(node);
    if (!ancestors.some((el) => SKIP_TAGS.has(el.tagName))) {
      tokens.push(...words(node.nodeValue, isStruck(ancestors)));
    }
  }
  return tokens;
};

const keyOf = (t) => `${t.struck ? '~' : ''}${t.text}`;
const joinWords = (x, y) => `${x} ${y}`;
const sameRun = (p, q) => p.changed === q.changed && p.struck === q.struck;

// → { oldParts, newParts }, each part { text, changed, struck }. Parts carry no
// surrounding whitespace — render a space between consecutive parts.
//   old: changed → removed (or its strike state changed)
//   new: changed → added   (struck + changed → newly struck through)
export const richWordDiff = (oldValue, newValue) => {
  const ops = diffSequence(toDiffTokens(oldValue), toDiffTokens(newValue), keyOf);

  const oldParts = coalesce(
    ops.filter((o) => o.op !== 'added')
      .map((o) => ({ text: o.oldToken.text, changed: o.op === 'removed', struck: o.oldToken.struck })),
    sameRun,
    joinWords
  );
  const newParts = coalesce(
    ops.filter((o) => o.op !== 'removed')
      .map((o) => ({ text: o.newToken.text, changed: o.op === 'added', struck: o.newToken.struck })),
    sameRun,
    joinWords
  );

  return { oldParts, newParts };
};

// → [{ text, struck }] runs for showing a whole value (added / removed rows).
export const toStruckRuns = (value) => coalesce(
  toDiffTokens(value),
  (p, q) => p.struck === q.struck,
  joinWords
);

// Plain text (strike dropped) — for emptiness checks and one-line previews.
export const toPlainText = (value) => toDiffTokens(value).map((t) => t.text).join(' ');
